// React and React Router
import { BrowserRouter, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { AppPresenter } from "./page/AppPresenter";
import { Flex, Loader } from "@mantine/core";

// Mantine
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import { notifications } from "@mantine/notifications";

// MUI
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import muiTheme from "./theme/muiTheme";

import { processUserLoginPure } from "./utils/pureDataFetch";
import { UserTypes } from "./contexts/UserContext";
import { installDebugTools } from "./utils/avatarDebugger";
import { setupGlobalAdminFunctions } from "./utils/adminSetup";

// Add this to imports
import { UserProvider, useUser, useUserDispatch } from "./contexts/UserContext";

// Remove moment import and add date-fns imports
import { format } from "date-fns";

// Add i18n import if not already present
import i18next from "i18next";

// Add this with other imports
import { useResetScroll } from "./hooks/useResetScroll";
import { initializeProjectFonts } from "./utils/fontCache";

// Add ThreadsContext imports
import {
  ThreadsProvider,
  useThreadsState,
  useThreadsDispatch,
  loadLifetimeDataWithTimeline,
} from "./contexts/ThreadsContext";
import { WritingModeProvider } from "./contexts/WritingModeContext";

function App() {
  // Add language handling
  useEffect(() => {
    const currentLang = i18next.language;
    if (currentLang.includes("en")) {
      i18next.changeLanguage("en");
    }
  }, []);

  // Initialize project fonts
  useEffect(() => {
    initializeProjectFonts();
  }, []);

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <UserProvider>
        <ThreadsProvider>
          <WritingModeProvider>
            <BrowserRouter>
              <AppContent />
            </BrowserRouter>
          </WritingModeProvider>
        </ThreadsProvider>
      </UserProvider>
    </ThemeProvider>
  );
}

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, isLogin, userLoading, error } = useUser();
  const dispatch = useUserDispatch();
  const [isDashboardLoad, setIsDashboardLoad] = useState(false);

  // Store the current path in localStorage whenever it changes
  // useEffect(() => {
  // 	if (isLogin) {
  // 		localStorage.setItem('lastPath', location.pathname);
  // 	}
  // }, [location.pathname, isLogin]);

  // On component mount, check if there's a stored path and navigate to it
  // useEffect(() => {
  // 	const lastPath = localStorage.getItem('lastPath');
  // 	if (isLogin && lastPath && lastPath !== location.pathname) {
  // 		navigate(lastPath);
  // 	}
  // }, [isLogin, navigate]);

  // Dashboard states - 이제 Dashboard.js에서 timeline으로 관리됨
  // 기존 monthlyInsight는 더 이상 필요없음

  // Page Initial Loading & Setting
  // Dashboard
  useEffect(() => {
    if (profile !== null) {
      setIsDashboardLoad(true);
    }
  }, [profile]);

  // 최초 로드 시에만 사용자 데이터 로드
  useEffect(() => {
    const loadUserData = async () => {
      try {
        const token = localStorage.getItem("accessToken");
        if (!token) {
          throw new Error("No access token found");
        }
        
        const profileData = await processUserLoginPure(token);
        
        dispatch({
          type: UserTypes.SET_PROFILE,
          payload: {
            profile: profileData,
            accessToken: token,
          },
        });
      } catch (error) {
        dispatch({
          type: UserTypes.CLEAR_PROFILE,
        });
        
        // 토큰 관련 에러인 경우 상세 로그 제공
        if (error.message === "No access token found") {
          console.warn("User not authenticated - redirecting to login");
        } else if (error.response?.status === 400) {
          console.warn("Invalid or expired access token - clearing authentication");
        } else {
          console.error("Error fetching user data:", error);
        }
      }
    };
    
    loadUserData();
    
    // 🆕 개발 환경에서 디버깅 도구 설치
    if (process.env.NODE_ENV === 'development') {
      installDebugTools();
      setupGlobalAdminFunctions();
    }
  }, [dispatch]); // dispatch만 의존성으로 설정 (최초 1회만 실행)

  // 라우터 보호 로직은 별도 useEffect로 분리
  useEffect(() => {
    
    if (!userLoading) {
      if (
        !isLogin &&
        location.pathname !== "/" &&
        location.pathname !== "/callback" &&
        location.pathname !== "/privacy" &&
        location.pathname !== "/terms" &&
        location.pathname !== "/admin" && // AdminRoute에서 처리하므로 제외
        location.pathname !== "/all-collection" &&
        !location.pathname.startsWith("/collection") &&
        !location.pathname.startsWith("/user")
      ) {
        navigate("/");
      }
    }
  }, [isLogin, location.pathname, userLoading]);

  // ThreadsContext states
  const state = useThreadsState();
  const { isLifetimeLoading, isLifetimeReady, lifetimeThreadsList } = state;
  const threadsDispatch = useThreadsDispatch();

  // 🔥 자동 업데이트: 로그인 직후 라우터 상관없이 실행
  useEffect(() => {
    if (isLogin && profile && !isLifetimeLoading && !isLifetimeReady) {
      console.log("🚀 자동 업데이트 시작: 로그인 후 라이프타임 데이터 로드 및 새 스레드 확인");
      const userID = localStorage.getItem("userID");
      
      loadLifetimeDataWithTimeline(threadsDispatch, userID, profile, true); // true = 자동 업데이트
    }
  }, [isLogin, profile, isLifetimeLoading, isLifetimeReady, threadsDispatch]);


  // 컬렉션 데이터는 MyPage에서 로드하도록 변경

  // Modify the existing location-based useEffect
  useEffect(() => {
    if (location.pathname === "/" && isLogin) {
      console.log("redirect to dashboard if login");
      navigate("/dashboard");
    }
  }, [location.pathname, isLogin, navigate]);

  // 월별 인사이트 업데이트 로직 제거됨 - Dashboard.js가 timeline으로 직접 관리

  // Add this line after your existing state declarations
  useResetScroll();

  return (
    <>
      {userLoading ? (
        <Flex justify={"center"} align={"center"} h={"100vh"} w={"100vw"}>
          <Loader />
        </Flex>
      ) : (
        <AppPresenter />
      )}
    </>
  );
}

export default App;
